import { CancelBtn } from 'components/Button/CancelBtn'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { Input } from 'components/Input/Input'
import { NavBar } from 'components/NavBar/NavBar'
import { Button } from 'components/ui/Button/Button'
import { createOperationTrigger } from 'features/operations/operationTriggersSlice'
import { ChangeEvent, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { AppDispatch } from 'store'
import { Box, Flex, H1WithBack, PageFooter, PageWrapper, Separator, Tabs, TabsContainer } from 'styling/GlobalStyles'
import { TitleText } from 'styling/Typography'
import { OpsTriggerMode } from 'types/operationsControl.interface'
import { ICreateOperationTriggerRequest } from 'types/api/operationsAimApi.interface'
import {
    OperationTriggerMetricSource,
    OperationTriggerMode,
    OperationTriggerOperator,
    OperationTriggerPriority,
    OperationTriggerScheduleType,
} from 'types/operationsEnums.interface'
import { IOperationTriggerFormErrors, IOperationTriggerFormState } from 'types/operationTriggers.interface'
import { FieldCol, FieldError, MockEngineNote } from './AddOperationTrigger.styled'

const MODE_TABS: { key: OpsTriggerMode; label: string }[] = [
    { key: 'COMPLETION', label: 'Completion' },
    { key: 'SCHEDULED', label: 'Scheduled' },
    { key: 'THRESHOLD', label: 'Threshold' },
    { key: 'EVENT', label: 'Event' },
]

const fireTypeOptions = [
    { label: 'On finish', value: 'ON_FINISH' },
    { label: 'Before finish', value: 'BEFORE_FINISH' },
]

const scheduleTypeOptions = [
    { label: 'Cron', value: 'CRON' },
    { label: 'Shift start', value: 'SHIFT_START' },
    { label: 'Shift end', value: 'SHIFT_END' },
    { label: 'Interval', value: 'INTERVAL' },
]

const metricSourceOptions = [
    { label: 'machine.fuelLevel', value: 'machine.fuelLevel' },
    { label: 'machine.engineHours', value: 'machine.engineHours' },
    { label: 'inventory.stockLevel', value: 'inventory.stockLevel' },
    { label: 'sensor.temperature', value: 'sensor.temperature' },
    { label: 'sensor.vibration', value: 'sensor.vibration' },
    { label: 'sensor.pressure', value: 'sensor.pressure' },
]

const operatorOptions = [
    { label: '<', value: 'LT' },
    { label: '>', value: 'GT' },
    { label: '<=', value: 'LTE' },
    { label: '>=', value: 'GTE' },
    { label: '=', value: 'EQ' },
]

const actionOptions = [
    { label: 'Create task', value: 'Create task' },
    { label: 'Create workflow', value: 'Create workflow' },
    { label: 'Raise issue', value: 'Raise issue' },
    { label: 'Notify role', value: 'Notify role' },
]

const priorityOptions = [
    { label: 'Normal', value: 'normal' },
    { label: 'High', value: 'high' },
    { label: 'Critical', value: 'critical' },
]

const OPERATOR_SYMBOL: { [k: string]: string } = {
    LT: '<',
    GT: '>',
    LTE: '<=',
    GTE: '>=',
    EQ: '=',
}

const initialForm: IOperationTriggerFormState = {
    name: '',
    mode: 'COMPLETION',
    parentTemplate: null,
    fireType: 'ON_FINISH',
    beforeMinutes: '',
    scheduleType: 'CRON',
    cron: '',
    intervalMinutes: '',
    metricSource: 'machine.fuelLevel',
    entityName: '',
    operator: 'LT',
    value: '',
    unit: '',
    cooldownMinutes: '60',
    eventCondition: '',
    action: 'Create task',
    actionTargetText: '',
    actionTargetTemplate: null,
    priority: 'normal',
}

const parseTasks = (data: any) => {
    return data ? data.data.map((item: any) => ({ label: item.name, value: item.id })) : []
}

export const AddOperationTrigger = () => {
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()
    const [form, setForm] = useState<IOperationTriggerFormState>(initialForm)
    const [submitted, setSubmitted] = useState(false)

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target
        setForm((prev) => ({ ...prev, [name]: value }))
    }

    const handleSelect = (v: any, n: string) => {
        setForm((prev) => ({ ...prev, [n]: v.value }))
    }

    const handleTemplate = (v: any, n: string) => {
        setForm((prev) => ({ ...prev, [n]: v?.value ? { label: v.label, value: v.value } : null }))
    }

    const errors: IOperationTriggerFormErrors = {
        name: !form.name.trim(),
        parentTemplate: form.mode === 'COMPLETION' && !form.parentTemplate,
        beforeMinutes:
            form.mode === 'COMPLETION' &&
            form.fireType === 'BEFORE_FINISH' &&
            (!form.beforeMinutes || Number(form.beforeMinutes) <= 0),
        cron: form.mode === 'SCHEDULED' && form.scheduleType === 'CRON' && !form.cron.trim(),
        intervalMinutes:
            form.mode === 'SCHEDULED' &&
            form.scheduleType === 'INTERVAL' &&
            (!form.intervalMinutes || Number(form.intervalMinutes) <= 0),
        entityName: form.mode === 'THRESHOLD' && !form.entityName.trim(),
        value: form.mode === 'THRESHOLD' && form.value.trim() === '',
        unit: form.mode === 'THRESHOLD' && !form.unit.trim(),
        cooldownMinutes:
            form.mode === 'THRESHOLD' && (form.cooldownMinutes.trim() === '' || Number(form.cooldownMinutes) < 0),
        eventCondition: form.mode === 'EVENT' && !form.eventCondition.trim(),
        actionTarget: form.action === 'Create task' ? !form.actionTargetTemplate : !form.actionTargetText.trim(),
    }
    const hasErrors = Object.values(errors).some(Boolean)

    const buildApiBody = (): ICreateOperationTriggerRequest | null => {
        const modeMap: { [k in OpsTriggerMode]?: OperationTriggerMode } = {
            COMPLETION: OperationTriggerMode.Completion,
            SCHEDULED: OperationTriggerMode.Scheduled,
            THRESHOLD: OperationTriggerMode.Threshold,
        }
        const triggerMode = modeMap[form.mode]
        if (!triggerMode) return null

        const priorityMap: { [k: string]: OperationTriggerPriority } = {
            low: OperationTriggerPriority.Low,
            normal: OperationTriggerPriority.Normal,
            high: OperationTriggerPriority.High,
            critical: OperationTriggerPriority.Critical,
        }
        const scheduleMap: { [k: string]: OperationTriggerScheduleType } = {
            CRON: OperationTriggerScheduleType.Cron,
            SHIFT_START: OperationTriggerScheduleType.ShiftStart,
            SHIFT_END: OperationTriggerScheduleType.ShiftEnd,
            INTERVAL: OperationTriggerScheduleType.Interval,
        }
        const operatorMap: { [k: string]: OperationTriggerOperator } = {
            LT: OperationTriggerOperator.LessThan,
            LTE: OperationTriggerOperator.LessThanOrEqual,
            GT: OperationTriggerOperator.GreaterThan,
            GTE: OperationTriggerOperator.GreaterThanOrEqual,
            EQ: OperationTriggerOperator.Equal,
        }
        const metricMap: { [k: string]: OperationTriggerMetricSource } = {
            'machine.fuelLevel': OperationTriggerMetricSource.MachineFuelLevel,
            'machine.engineHours': OperationTriggerMetricSource.MachineEngineHours,
            'inventory.stockLevel': OperationTriggerMetricSource.InventoryStockLevel,
            'sensor.temperature': OperationTriggerMetricSource.Sensor,
            'sensor.vibration': OperationTriggerMetricSource.Sensor,
            'sensor.pressure': OperationTriggerMetricSource.Sensor,
        }

        const body: ICreateOperationTriggerRequest = {
            name: form.name.trim(),
            triggerMode,
            scope: 'Site-wide',
            action: form.action,
            priority: priorityMap[form.priority] ?? OperationTriggerPriority.Normal,
            enabled: true,
        }

        if (form.mode === 'COMPLETION' && form.parentTemplate?.value) {
            body.taskTemplateId = Number(form.parentTemplate.value)
        }
        if (form.mode === 'SCHEDULED') {
            body.scheduleType = scheduleMap[form.scheduleType]
            if (form.scheduleType === 'CRON') body.cron = form.cron.trim()
            if (form.scheduleType === 'INTERVAL') body.intervalMinutes = Number(form.intervalMinutes)
        }
        if (form.mode === 'THRESHOLD') {
            body.metricSource = metricMap[form.metricSource]
            body.operator = operatorMap[form.operator]
            body.thresholdValue = Number(form.value)
            body.unit = form.unit.trim()
            body.cooldownMinutes = Number(form.cooldownMinutes)
        }
        return body
    }

    const handleSave = () => {
        setSubmitted(true)
        if (hasErrors) {
            toast.warn('Fill in the required fields')
            return
        }
        if (form.mode === 'EVENT') {
            toast.warn('Event triggers are not supported by the API yet')
            return
        }
        const body = buildApiBody()
        if (!body) return
        dispatch(createOperationTrigger(body))
        navigate('/operations/triggers')
    }

    const renderCompletion = () => (
        <Flex column gap='1rem'>
            <FieldCol>
                <HonestSelectAsyncInput
                    link='task/template/overviews'
                    labelText='Parent Task Template'
                    onChange={handleTemplate}
                    name='parentTemplate'
                    placeholder='Search Task Templates'
                    parserFunction={parseTasks}
                    defaultValue={form.parentTemplate?.label}
                    required
                />
                {submitted && errors.parentTemplate && <FieldError>Parent task template is required</FieldError>}
            </FieldCol>
            <Flex gap='1rem'>
                <HonestSelectInput
                    name='fireType'
                    options={fireTypeOptions}
                    defaultValue={form.fireType}
                    onChange={handleSelect}
                    labelText='Fire type'
                    noX
                />
                {form.fireType === 'BEFORE_FINISH' && (
                    <FieldCol>
                        <Input
                            labelText='Minutes before finish'
                            name='beforeMinutes'
                            type='number'
                            value={form.beforeMinutes}
                            onChange={handleChange}
                            placeholder='15'
                            invalid={submitted && errors.beforeMinutes}
                            noinvalidtext
                        />
                        {submitted && errors.beforeMinutes && <FieldError>Minutes before finish is required</FieldError>}
                    </FieldCol>
                )}
            </Flex>
        </Flex>
    )

    const renderScheduled = () => (
        <Flex column gap='1rem'>
            <HonestSelectInput
                name='scheduleType'
                options={scheduleTypeOptions}
                defaultValue={form.scheduleType}
                onChange={handleSelect}
                labelText='Schedule type'
                noX
            />
            {form.scheduleType === 'CRON' && (
                <FieldCol>
                    <Input
                        labelText='Cron expression'
                        name='cron'
                        value={form.cron}
                        onChange={handleChange}
                        placeholder='0 6 * * *'
                        invalid={submitted && errors.cron}
                        noinvalidtext
                    />
                    {submitted && errors.cron && <FieldError>Cron expression is required</FieldError>}
                </FieldCol>
            )}
            {form.scheduleType === 'INTERVAL' && (
                <FieldCol>
                    <Input
                        labelText='Interval minutes'
                        name='intervalMinutes'
                        type='number'
                        value={form.intervalMinutes}
                        onChange={handleChange}
                        placeholder='30'
                        invalid={submitted && errors.intervalMinutes}
                        noinvalidtext
                    />
                    {submitted && errors.intervalMinutes && <FieldError>Interval minutes is required</FieldError>}
                </FieldCol>
            )}
        </Flex>
    )

    const renderThreshold = () => (
        <Flex column gap='1rem'>
            <Flex gap='1rem'>
                <HonestSelectInput
                    name='metricSource'
                    options={metricSourceOptions}
                    defaultValue={form.metricSource}
                    onChange={handleSelect}
                    labelText='Metric source'
                    noX
                />
                <FieldCol>
                    <Input
                        labelText='Entity name'
                        name='entityName'
                        value={form.entityName}
                        onChange={handleChange}
                        placeholder='All haul trucks'
                        invalid={submitted && errors.entityName}
                        noinvalidtext
                    />
                    {submitted && errors.entityName && <FieldError>Entity name is required</FieldError>}
                </FieldCol>
            </Flex>
            <Flex gap='1rem'>
                <HonestSelectInput
                    name='operator'
                    options={operatorOptions}
                    defaultValue={form.operator}
                    onChange={handleSelect}
                    labelText='Operator'
                    noX
                />
                <FieldCol>
                    <Input
                        labelText='Value'
                        name='value'
                        type='number'
                        value={form.value}
                        onChange={handleChange}
                        placeholder='20'
                        invalid={submitted && errors.value}
                        noinvalidtext
                        allowZero
                    />
                    {submitted && errors.value && <FieldError>Value is required</FieldError>}
                </FieldCol>
                <FieldCol>
                    <Input
                        labelText='Unit'
                        name='unit'
                        value={form.unit}
                        onChange={handleChange}
                        placeholder='%, h, °C'
                        invalid={submitted && errors.unit}
                        noinvalidtext
                    />
                    {submitted && errors.unit && <FieldError>Unit is required</FieldError>}
                </FieldCol>
                <FieldCol>
                    <Input
                        labelText='Cooldown minutes'
                        name='cooldownMinutes'
                        type='number'
                        value={form.cooldownMinutes}
                        onChange={handleChange}
                        placeholder='60'
                        invalid={submitted && errors.cooldownMinutes}
                        noinvalidtext
                        allowZero
                    />
                    {submitted && errors.cooldownMinutes && <FieldError>Cooldown minutes is required</FieldError>}
                </FieldCol>
            </Flex>
        </Flex>
    )

    const renderEvent = () => (
        <FieldCol>
            <Input
                labelText='Condition'
                name='eventCondition'
                value={form.eventCondition}
                onChange={handleChange}
                placeholder='incident.severity >= L3'
                invalid={submitted && errors.eventCondition}
                noinvalidtext
            />
            {submitted && errors.eventCondition && <FieldError>Condition is required</FieldError>}
        </FieldCol>
    )

    const renderMode = () => {
        if (form.mode === 'COMPLETION') return renderCompletion()
        if (form.mode === 'SCHEDULED') return renderScheduled()
        if (form.mode === 'THRESHOLD') return renderThreshold()
        return renderEvent()
    }

    return (
        <>
            <NavBar />
            <PageWrapper>
                <H1WithBack arrow title='Add Operation Trigger' />
                <Box style={{ marginTop: '1rem' }}>
                    <FieldCol>
                        <Input
                            labelText='Name'
                            name='name'
                            value={form.name}
                            onChange={handleChange}
                            placeholder='Enter name'
                            invalid={submitted && errors.name}
                            noinvalidtext
                        />
                        {submitted && errors.name && <FieldError>Name is required</FieldError>}
                    </FieldCol>
                    <TitleText style={{ marginTop: '1rem' }}>Mode</TitleText>
                    <TabsContainer style={{ margin: '1rem 0' }}>
                        {MODE_TABS.map((t) => (
                            <Tabs key={t.key} active={form.mode === t.key} onClick={() => setForm((prev) => ({ ...prev, mode: t.key }))}>
                                {t.label}
                            </Tabs>
                        ))}
                    </TabsContainer>
                    {renderMode()}
                    <Separator />
                    <TitleText>Action</TitleText>
                    <Flex gap='1rem' style={{ margin: '1rem 0' }}>
                        <HonestSelectInput
                            name='action'
                            options={actionOptions}
                            defaultValue={form.action}
                            onChange={handleSelect}
                            labelText='Type'
                            noX
                        />
                        <HonestSelectInput
                            name='priority'
                            options={priorityOptions}
                            defaultValue={form.priority}
                            onChange={handleSelect}
                            labelText='Priority'
                            noX
                        />
                    </Flex>
                    {form.action === 'Create task' ? (
                        <FieldCol>
                            <HonestSelectAsyncInput
                                link='task/template/overviews'
                                labelText='Action target'
                                onChange={handleTemplate}
                                name='actionTargetTemplate'
                                placeholder='Search Task Templates'
                                parserFunction={parseTasks}
                                defaultValue={form.actionTargetTemplate?.label}
                                required
                            />
                            {submitted && errors.actionTarget && <FieldError>Action target is required</FieldError>}
                        </FieldCol>
                    ) : (
                        <FieldCol>
                            <Input
                                labelText='Action target'
                                name='actionTargetText'
                                value={form.actionTargetText}
                                onChange={handleChange}
                                placeholder={form.action === 'Notify role' ? 'Shift supervisor' : 'Enter target'}
                                invalid={submitted && errors.actionTarget}
                                noinvalidtext
                            />
                            {submitted && errors.actionTarget && <FieldError>Action target is required</FieldError>}
                        </FieldCol>
                    )}
                </Box>
            </PageWrapper>
            <PageFooter>
                <Flex between center gap='1rem'>
                    <MockEngineNote>This trigger will run on the mock engine until backend V2.</MockEngineNote>
                    <Flex gap='1rem'>
                        <CancelBtn />
                        <Button variant='primary' onClick={handleSave}>
                            Save
                        </Button>
                    </Flex>
                </Flex>
            </PageFooter>
        </>
    )
}
